import React, { FC, useState } from 'react'
import { Dialog } from '@blueprintjs/core'
import { ShadowButtonStyled } from '@ui/buttons/Button'
import BookingCalendar from '../BookingCalendar'
import css from './BookingCalendarPopup.module.scss'
import useSettings from '../NewSettings/hooks/useSettings'
import useBookingDatesFromQParams from './hooks/useBookingDatesFromQParams'
import { useBookingCalendar } from './hooks'
import { Flex, Icon } from '@ui'
import { DateUtil, createDisabledDate, getMaxDateByTimezone, isFinder } from '@utils'
import { Button } from '@runeffective/evo-design-system'
import { UseFloorHook, UseFloorProvider, useFloor } from '@providers/mapProvider'
import { ICurrentUser, IUser } from '@api'
import { BookDeskParamT } from '@providers'

type BookingCalendarPopupPropsT = {
  value?: string
  title?: string | React.ReactNode
  timezone: string
  selected?: DateUtil[]
  isOpen?: boolean
  updateHandler: (param: (DateUtil | any)[]) => void
  onClose?: () => void
  children?: JSX.Element
  isNotBookDesk?: boolean
  bookDeskHandler?: (param: (DateUtil | any)[]) => void
  floor?: UseFloorHook
}

const BookingCalendarPopup: FC<BookingCalendarPopupPropsT> = (props) => {
  const [dates, setDates] = useState<DateUtil[]>(props.selected || [])
  const [isPopoverOpen, setIsPopoverOpen] = useState(props.isOpen || false)
  const { maxFutureBookingDuration, bookingWorkdayTimeFrom, bookingWorkdayTimeTo } = useSettings()
  const { fromDate, toDate } = useBookingDatesFromQParams()
  const {
    bookingWorkdayTimeFrom: floorBookingWorkdayTimeFrom,
    bookingWorkdayTimeTo: floorBookingWorkdayTimeTo,
  } = useFloor()
  const { recurringSetting, setRecurredBooking, allDaySetting, recurrence, updateBookingDate } =
    useBookingCalendar()

  const maxDuration = maxFutureBookingDuration || { days: 1 }
  const selectedDates = props.selected || [fromDate, toDate]
  const maxDate = getMaxDateByTimezone(props.timezone, maxDuration)
  const lastBookDate = createDisabledDate(props.timezone, maxDuration)

  const openPopOver = () => {
    setIsPopoverOpen(true)
  }

  const closePopOver = () => {
    setIsPopoverOpen(false)
    props.onClose?.()
  }

  return (
    <>
      <ShadowButtonStyled
        className={css.button}
        data-test="booking-calendar-button"
        ghost={false}
        text={props.isNotBookDesk ? 'Book Date' : props.value || 'Book'}
        onClick={openPopOver}
      />
      <div className={css.popover_content}>
        <Dialog
          className={props.isNotBookDesk ? css.book_date : css.popover}
          isOpen={isPopoverOpen}
          title={props.title}
          onClose={closePopOver}>
          <div>
            <div>
              <div>
                <BookingCalendar
                  selectedDates={selectedDates}
                  allDaySetting={allDaySetting}
                  timezone={props.timezone}
                  maxSelectedDays={5}
                  defaultTimeFrom={floorBookingWorkdayTimeFrom || bookingWorkdayTimeFrom}
                  defaultTimeTo={floorBookingWorkdayTimeTo || bookingWorkdayTimeTo}
                  options={[]}
                  headerRender={
                    props.isNotBookDesk ? (
                      <Flex align="baseline" className={css.info_block}>
                        <Icon name="info-circle" />
                        <span>Bookings are available through {maxDate}.</span>
                      </Flex>
                    ) : (
                      <Flex align="baseline" className={css.info_block}>
                        <Icon name="info-circle" />
                        <span>
                          Bookings are available through {maxDate}. This action will override your
                          previous preferences.
                        </span>
                      </Flex>
                    )
                  }
                  numOfViews={props.isNotBookDesk ? 1 : 2}
                  enableMultipleDays
                  bookedTimes={[]}
                  constraints={{ last: lastBookDate }}
                  recurrence={recurrence}
                  is24Hr={true}
                  setRecurredBooking={setRecurredBooking}
                  onTimeChange={({ fromDate, toDate }) => {
                    setDates([fromDate, toDate])
                  }}
                  showTime={true}
                  showRecurrence={recurringSetting}
                  validateDateTime={true}
                  onDateChange={(dates) => {
                    setDates(dates)
                  }}
                  floor={props.floor as UseFloorHook}
                  isNotBookDesk={props.isNotBookDesk}
                />
              </div>
              {props.children && <div>{props.children}</div>}
            </div>
            <div style={{ textAlign: 'right' }}>
              <Button
                size="small"
                layout="borderless"
                style={{ display: 'inline' }}
                onClick={() => closePopOver()}>
                Discard
              </Button>
              {props.isNotBookDesk ? (
                <Button
                  style={{
                    textAlign: 'right',
                    display: 'inline',
                    marginLeft: 20,
                    padding: '6px 10px',
                  }}
                  size="small"
                  layout="primary"
                  // disabled={isBookNowDisabled}
                  onClick={() => {
                    props.bookDeskHandler?.(dates.length ? dates : selectedDates)
                  }}>
                  Book
                </Button>
              ) : (
                <Button
                  style={{
                    textAlign: 'right',
                    display: 'inline',
                    marginLeft: 20,
                    padding: '6px 10px',
                  }}
                  size="small"
                  layout="primary"
                  // disabled={isBookNowDisabled}
                  onClick={() => {
                    closePopOver()
                    props.updateHandler(dates.length ? dates : selectedDates)
                  }}>
                  Update
                </Button>
              )}
            </div>
          </div>
        </Dialog>
      </div>
    </>
  )
}

export default BookingCalendarPopup
